
import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import {
  esriGeometryEngine,
  Graphic,
  GraphicsLayer,
  Point,
  Polyline,
  SketchViewModel,
  MapView,
  SpatialReference,
  FeatureLayer,
  Extent,
} from 'cd-eft/utils/esriTypes';
import stores from 'cd-eft/models/Stores';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import { DynamicWaterwayHub } from './DynamicWaterwayHub';
import { DynamicProfileStoreBase } from '../DynamicProfileStoreBase';
import { DynamicProfileClass } from '../DynamicProfileClass';
import { DynamicWaterwayTableStore } from './DynamicWaterwayTableStore';
import { ProfileKeyCode, SketchState } from '../DynamicProfileEnums';
import { doEvents } from 'cd-eft/utils/sysUtils';
import { AppConstants } from 'cd-eft/appConstants';
import { radiansToDegrees, toDecimalPrecision } from 'cd-eft/utils/mathUtilities';
import { reachLineSymbol, reachHighlightSymbol, reachStationSymbol, createAiActionArrow, createAiActionLabel } from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { parseStationString } from 'cd-eft/utils/stationUtilities';
import sleep from 'cd-eft/utils/sleep';


const { designStore, waterwayDesignStore, threeCrossSectionDesignStore } = stores.AppStore;

// TODO: to clean up.
const ERROR_SELF_CROSSING = 'Common_29';

class RubberbandSketchViewModel {
  private _rubberbandSketchLayer: GraphicsLayer | undefined = void 0;
  private startPoint: Point | undefined = void 0;
  public _profileView: MapView | undefined = void 0;
  private viewPointerMoveHandle;
  private viewImmediateClickHandle;

  constructor(mapView: MapView, sketchLayer: GraphicsLayer) {
    this._profileView = mapView;
    this._rubberbandSketchLayer = sketchLayer;
  }

  public create(startPoint?: Point) {
    this.startPoint = startPoint;
    this.viewImmediateClickHandle = this._profileView!.on('immediate-click', this.viewImmediateClickHandler);
    this.viewPointerMoveHandle = this._profileView!.on('pointer-move', this.viewPointerMoveHandler);
  }

  public complete() {
    this.viewPointerMoveHandle?.remove();
    this.viewImmediateClickHandle?.remove();
    this.rubberbandSketchLayer.removeAll();
  }

  protected get rubberbandSketchLayer() {
    return this._rubberbandSketchLayer!;
  }

  private viewPointerMoveHandler = async (event: __esri.ViewPointerMoveEvent) => {
    this.rubberbandSketchLayer.removeAll();
    const movingPoint = this._profileView?.toMap(event);

    if (this.startPoint && movingPoint) {
      const rubberBandLine = new Polyline({ spatialReference: this._profileView!.spatialReference, hasZ: true });
      rubberBandLine.addPath([this.startPoint, movingPoint]);

      const rubberBandGraphic = new Graphic({ geometry: rubberBandLine, symbol: reachLineSymbol.designLineSymbol });
      this.rubberbandSketchLayer.add(rubberBandGraphic);
    }
  };

  private viewImmediateClickHandler = async (event: __esri.ViewImmediateClickEvent) => {
    this.complete();
  };
}

class DynamicWaterwayProfileStore extends DynamicProfileStoreBase {
  public hub: DynamicWaterwayHub;

  private profileInEditing: DynamicProfileClass | undefined;
  private dataSpatialRef: SpatialReference | undefined = void 0;
  private viewPointerMoveHandle;
  private viewPointerLeaveHandle;

  private stationVertices = [] as number[][];
  public alignmentCenterLine = [] as number[][];
  private extendSketchVM: SketchViewModel | undefined = void 0;
  private insertPointStatus: 'startPoint' | 'stopPoint' | 'regular' = 'regular';
  private rubberbandSketchVm: RubberbandSketchViewModel | undefined;
  private addWDTEdits: any;
  private endStation_X = 0;
  private validStartStation = true;
  private centerline: any;

  private isAfterReshapeVertexAdd = false;
  private _profileTrackLayer: GraphicsLayer | undefined = void 0;
  private saveUiState;
  private getUiState;

  /**
   *
   */
  constructor(hub: DynamicWaterwayHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.WCL, ProfileKeyCode.WLB, ProfileKeyCode.WRB, ProfileKeyCode.WLTB, ProfileKeyCode.REACH];
  }

  // Abstract methods
  async createChannelFromSavedUgo(): Promise<void> { }
  async updateUgoTableOnChangeStationValue(): Promise<void> { }
  async redrawUgoProfiles(): Promise<void> { }

  protected get stationHitTestOptions(): __esri.MapViewHitTestOptions | undefined {
    const hitLayer = this._profileView?.map.findLayerById("reachStations");
    if (hitLayer) {
      const options: __esri.MapViewHitTestOptions = {};
      options.include = hitLayer;
      return options;
    }
    return undefined;
  }

  updateGridValues(gridValue: number, updateLines: boolean = true) {
    this.updateUserGridValues(gridValue, updateLines);
  }

  protected async generateWaterwayProfiles(lowBank, bankOffset, sampleInterval, verticalInterval) {
    try {
      this.raiseStartLengthyProcessEvent(true);
      await doEvents();

      const profiles = await this.getProfileData(lowBank, bankOffset, sampleInterval, verticalInterval, waterwayDesignStore.alignedDesignElementId);

      if (profiles) {
        // Get ymax and ymin
        const centerlineProfile = this.profiles[ProfileKeyCode[4]] as DynamicProfileClass;
        const centerlineProfileStations = profiles.data.centerlineProfile?.stationDTOList;
        if (centerlineProfileStations?.length && centerlineProfileStations.length > 0) {
          this._centerlineStations = [...centerlineProfileStations];
          let yValues;

          // Draw bank(s)
          const bankProfileList = profiles.data.bankProfileList ?? [];
          if (bankProfileList.length === 1) {
            const lowBankStations = bankProfileList[0]?.stationDTOList;
            yValues = await this.getMaxMinYValues([centerlineProfileStations, lowBankStations], this.alignmentCenterLine);
            if (lowBankStations?.length && lowBankStations.length > 0) {
              this._lowBankStations = [...lowBankStations];
              const lowBankProfile = this.profiles[ProfileKeyCode[5]] as DynamicProfileClass;
              lowBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
              lowBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
              lowBankProfile.mapGroundProfilePoints(lowBankStations);
              const totalDist = lowBankStations[lowBankStations.length - 1].distance;
              if (totalDist) lowBankProfile.drawProfileGraphics(totalDist);
            }
          } else {
            const rightBankStations = bankProfileList[0]?.stationDTOList;
            const leftBankStations = bankProfileList[1]?.stationDTOList;
            yValues = await this.getMaxMinYValues([centerlineProfileStations, rightBankStations, leftBankStations], this.alignmentCenterLine);

            if (rightBankStations?.length && rightBankStations.length > 0) {
              this._rightBankStations = [...rightBankStations];
              const rightBankProfile = this.profiles[ProfileKeyCode[6]] as DynamicProfileClass;
              rightBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
              rightBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
              rightBankProfile.mapGroundProfilePoints(rightBankStations);
              const totalDist = rightBankStations[rightBankStations.length - 1].distance;
              if (totalDist) rightBankProfile.drawProfileGraphics(totalDist);
            }

            if (leftBankStations?.length && leftBankStations.length > 0) {
              this._leftBankStations = [...leftBankStations];
              const leftBankProfile = this.profiles[ProfileKeyCode[7]] as DynamicProfileClass;
              leftBankProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
              leftBankProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
              leftBankProfile.mapGroundProfilePoints(leftBankStations);
              const totalDist = leftBankStations[leftBankStations.length - 1].distance;
              if (totalDist) leftBankProfile.drawProfileGraphics(totalDist);
            }
          }

          // Draw centerline
          centerlineProfile.ymax = yValues.ymax ? yValues.ymax : undefined;
          centerlineProfile.ymin = yValues.ymin ? yValues.ymin : undefined;
          this.endStation_X = centerlineProfileStations.slice(-1)[0].distance!;
          waterwayDesignStore.endStation_XCenterLine = this.endStation_X;
          centerlineProfile.mapGroundProfilePoints(centerlineProfileStations);
          const totalDist = centerlineProfileStations[centerlineProfileStations.length - 1].distance;
          if (totalDist) centerlineProfile.drawProfileGraphics(totalDist);

          await this.hub.MapStore?.setAlignment(centerlineProfileStations);

          this.viewPointerMoveHandle = this._profileView!.on('pointer-move', (async (e: __esri.ViewPointerMoveEvent) => {
            const point = this._profileView?.toMap({ x: e.x, y: e.y });
            if (point) {
              this.hub.MapStore?.setTracker(point.x / (designStore.commonHorizontalStretchFactor || 1), point.y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor));
            }
            if (this.stationHitTestOptions) {
              const result = await this._profileView?.hitTest(e, this.stationHitTestOptions);
              if (result?.results[0]) {
                const stationStation = result?.results[0].graphic.attributes.Station;
                if (stationStation) {
                  let stationIndex = this.hub.waterwayStore.waterwayDesignObject.waterwayDesignStations.findIndex(s =>
                    s.station === stationStation);
                  if (stationIndex === 0) {
                    stationIndex = 1;
                  }
                  if ((stationIndex > -1) && (stationIndex < this.hub.waterwayStore.waterwayDesignObject.waterwayDesignStations.length)) {
                    this.trackStation(this.hub.waterwayStore.waterwayDesignObject.waterwayDesignStations[stationIndex]);
                    if (this.hub.TableStore) {
                      (this.hub.TableStore as DynamicWaterwayTableStore).highlightRows(stationIndex);
                    }
                  }
                }
              } else {
                this.unTrackUnselectedProfilePoints();
                if (this.hub.TableStore) {
                  (this.hub.TableStore as DynamicWaterwayTableStore).clearHighlights();
                }
              }
            }
          }));

          this.viewPointerLeaveHandle = this._profileView!.on('pointer-leave', (e: __esri.ViewPointerMoveEvent) => {
            this.hub.MapStore?.clearTracker();
          });

          await new Promise<void>(resolve => setTimeout(() => {
            this.centerline = centerlineProfile.groundProfileLayer.graphics.getItemAt(0).geometry as Polyline;
            resolve();
          }, 1000));

          // Draw Guardrail
          waterwayDesignStore.waterwayDesignObject.startGuardrail = toDecimalPrecision(waterwayDesignStore.startGradeLength, 1);
          waterwayDesignStore.waterwayDesignObject.endGuardrail = toDecimalPrecision((this.endStation_X - waterwayDesignStore.endGradeLength), 1);
          await this.drawGuardrail();
        }
      }
    } catch {
    } finally {
      this.raiseStartLengthyProcessEvent(false);
    }
  }

  protected initSketchVm(): void {
    // if (this.mainSketchVm && this.profileInEditing) {
    if (this.mainSketchVm && this.sketchLayer) {
      this.mainSketchVm.polylineSymbol = reachLineSymbol.designLineSymbol;
      this.mainSketchVm.view = this._profileView!;
      this.mainSketchVm.layer = this.sketchLayer;
      this.mainSketchVm.updateOnGraphicClick = false;
      this.mainSketchVm.defaultUpdateOptions = {
        // set the default options for the update operations
        toggleToolOnClick: false, // only reshape operation will be enabled
      };
    }
  }

  async generateCenterlineAndBank(lowBank, bankOffset, sampleInterval, verticalInterval) {
    this.profileKeys.forEach((keyCode) => {
      this.reset(keyCode);
    });
    this._centerlineStations = undefined;
    this._lowBankStations = undefined;
    this._leftBankStations = undefined;
    this._rightBankStations = undefined;
    designStore.commonHorizontalStretchFactor = undefined;
    designStore.commonVerticalStretchFactor = undefined;
    await this.generateWaterwayProfiles(lowBank, bankOffset, sampleInterval, verticalInterval);
    this.setUpSketchCapability();
  }

  LoadProfile(tableObject: any, keyCode: ProfileKeyCode) { }
  protected setUpTerraceAlignmentView(event: any): void { }

  public removeSketch(): void {
    this.unTrackProfilePoints();
    this.clearDelete();
    this.removeReachStationLayer();
    this.removeMidPointStationLayer();
    this.sketchLayer?.graphics.removeAll();
    this.stationVertices.length = 0;
    designStore.reachStationsLayerExists = false;
    this.clearCrossSectionWaterwayStations();
    this.hub.MapStore?.clearReachStations();
  }

  sketchPolyline(saveUiState?, getUiState?) {
    if (saveUiState) this.saveUiState = saveUiState;
    if (getUiState) this.getUiState = getUiState;
    this.removeSketch();
    this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[4] });
    this.mainSketchVm?.create('polyline');
    this.setSketchLayer();
  }

  SketchPoint(propAddEdits?: any) {
    this.addWDTEdits = propAddEdits;
    this.clearDelete();
    if (this.mainSketchVm) {
      this.mainSketchVm!.snappingOptions = {
        distance: 21,
        enabled: true,
        featureSources: [{ layer: this.sketchLayer }] as any,
      } as any;
    }

    this.mainSketchVm?.create('point');
    this.generateReachStationLayer(true);
    this.changePointSymbol('editableReachStation');

  }

  reshapePolyline(propAddEdits?: any) {
    this.addWDTEdits = propAddEdits;
    this.generateReachStationLayer(true);
    this.clearDelete();
    if (this.mainSketchVm?.layer && this.mainSketchVm.layer.graphics && this.mainSketchVm.layer.graphics.length > 0) {
      this.mainSketchVm?.update([this.mainSketchVm.layer.graphics.getItemAt(0)], {
        tool: 'reshape',
        enableRotation: false,
        enableScaling: false,
        preserveAspectRatio: false,
        toggleToolOnClick: false,
      });
      this.isAfterReshapeVertexAdd = false;
    }

  }

  stopEditing() {
    this.clearDelete();
    this.mainSketchVm?.complete();
    this.createReachLineFromSavedWaterway();
  }

  async handlePolylineCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    const profileMinY = ((this.maxExtent?.ymin as number) + this.y_padding);
    if (event.state === 'start' || event.state === 'active') {
      if (event.toolEventInfo.type === 'vertex-add') {
        let x = event.toolEventInfo.vertices[0].coordinates[0];
        const stretchedX = x;
        x = designStore.commonHorizontalStretchFactor ? x / designStore.commonHorizontalStretchFactor : x;
        const y = event.toolEventInfo.vertices[0].coordinates[1];
        const index = event.toolEventInfo.vertices[0].vertexIndex;

        if (x < waterwayDesignStore.waterwayDesignObject.startGuardrail || x > waterwayDesignStore.waterwayDesignObject.endGuardrail || y < profileMinY) {
          // if the first or second point is invalid, remove the entire sketch, otherwise delete the invalid point
          if (index == 0 || index == 1) {
            this.validStartStation = false;
            this.mainSketchVm?.complete();
            this.sketchLayer?.graphics.removeAll();
          } else {
            const length = (event.graphic.geometry as Polyline).paths[0].length;
            await this.deleteReachVertex({ index: length - 1 });
          }
          await MessageCenter.addWarningFromCode('WDT_73');
        } else {
          if (this.checkAboveCenterline(stretchedX, y, this.centerline)) {
            MessageCenter.addWarningFromCode('WDT_106');
          }
          if (this.verifyDistanceWithinLimit(stretchedX, y, 2, this.centerline)) {
            MessageCenter.addWarningFromCode('WDT_110');
          }
          if (this._isValidLine) {
            const line = event.graphic.geometry as Polyline;
            if (this.isValidLastSegment(line)) {
              this.clearInvalidState(event.graphic);
              this.stationVertices.push([x, y]);
            } else {
              event.graphic.symbol = reachLineSymbol.invalidLineSymbol;
              MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
            }
          } else {
            // This is for the case of "slow" twice clicks
            this.mainSketchVm?.complete();
            const length = (event.graphic.geometry as Polyline).paths[0].length;
            await this.deleteReachVertex({ index: length - 1 });
            event.graphic.symbol = reachLineSymbol.designLineSymbol;
            this._isValidLine = true;
          }
        }
      } else if (event.toolEventInfo.type === 'cursor-update') {
        if (!this._isValidLine) {
          this.mainSketchVm?.undo();
          this.clearInvalidState(event.graphic, 1500);
        }
      }
    }

    if (event.state === 'complete') {

      if (!this._isValidLine) {
        const length = (event.graphic.geometry as Polyline).paths[0].length;
        await this.deleteReachVertex({ index: length - 1 });
        this.clearInvalidState(void 0, 1500);
      }

      if (this.validStartStation) {
        if (this.validateDirection(this.stationVertices)) {
          // Calculate the angle between the alignment centerline  and the waterway
          if (this.stationVertices.length >= 2) {
            const isAngleValid = this.validateAngleBetweenLines(this.stationVertices, this.alignmentCenterLine);
            if (!isAngleValid) {
              await MessageCenter.addErrorFromCode('WDT_105');
              this.sketchLayer?.graphics.removeAll();
            }
          }
          if (!this.validateLastStation(this.stationVertices, profileMinY)) {
            const length = (event.graphic.geometry as Polyline).paths[0].length;
            await this.deleteReachVertex({ index: length - 1 });
            await MessageCenter.addWarningFromCode('WDT_73');
          }
          await this.saveUiState('save');
          await this.getUiState();
          this.broadcastNewVertexInfo(this.stationVertices);
          // NOTE: we now generate the reach layer in tableStore after station rows have been calculated
          designStore.reachStationsLayerExists = true;
        } else {
          await MessageCenter.addErrorFromCode('WDT_72');
          this.sketchLayer?.graphics.removeAll();
        }
      } else {
        this.validStartStation = true;
      }

      this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[4] });
      this.stationVertices.length = 0;
    }

    if (event.state === 'cancel') {
      this.broadcastSketchState({ state: SketchState.CANCEL, keyCode: this.profileKeys[4] });
      this.stationVertices.length = 0;
    }
  }

  async handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'complete' && event.graphic && event.graphic.geometry) {
      const insertPoint = event.graphic.geometry as Point;
      const sr = event.graphic.geometry.spatialReference;

      if (this.sketchLayer?.graphics && this.sketchLayer.graphics.length > 1) {
        const waterWay = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
        // Check if the polyline contains the point
        if (!esriGeometryEngine.contains(waterWay, insertPoint)) {
          // If the polyline doesn't contain the point, display the error message
          await MessageCenter.addErrorFromCode('WDT_104');
        }
        const path = waterWay?.paths[0];

        const [startPoint, stopPoint] = this.getStartAndStopPoint(waterWay);

        if (esriGeometryEngine.distance(insertPoint, startPoint) === 0) {
          this.mainSketchVm?.create('point');
          this.insertPointStatus = 'startPoint';
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (esriGeometryEngine.distance(insertPoint, stopPoint) === 0) {
          this.extendSketchVM?.create('point');
          this.insertPointStatus = 'stopPoint';
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (this.isSnappedOnVertex(insertPoint, waterWay)) {
          this.insertPointStatus = 'regular';
        } else {
          this.insertPointStatus = 'regular';



          if (path) {
            let pointIndex = -1;
            let beginPoint = path[0];
            for (let index = 1; index < path.length; index++) {
              const endPoint = path[index];
              const polyline = new Polyline({ paths: [[beginPoint, endPoint]], spatialReference: sr });
              if (esriGeometryEngine.intersects(polyline, insertPoint)) {
                pointIndex = index;
                break;
              }
              beginPoint = endPoint;
            }
            // prepare for profile view
            const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
            const dataX = (insertPoint as Point).x / xScale;
            const dataY = (insertPoint as Point).y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);



            if (pointIndex >= 0) {

              if (path[0][0] > path[path.length - 1][0]) {
                //digitized right to left, correct the order index for row.
                const rowIndex = (path.length) - pointIndex;
                waterWay.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, rowIndex, this.profileKeys[0], this.addWDTEdits);
              }
              else {
                waterWay.insertPoint(0, pointIndex, insertPoint);
                this.broadcastInsertVertexInfo(newVertex, pointIndex, this.profileKeys[0], this.addWDTEdits);
              }
            }
          }
        }
      }

      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
      this.sketchLayer?.graphics.pop();
    } else if (event.state === "complete" || event.state == "cancel") {
      this.changePointSymbol('reachStation');
      if (this.mainSketchVm) this.mainSketchVm!.snappingOptions = {} as any;
    }
  }

  async handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    // this.removeReachStationLayer(); 
    if (event.toolEventInfo) {
      if (event.toolEventInfo.type.includes('reshape-stop')) {
        const line = event.graphics[0].geometry as Polyline;
        if (this.isValidFromStart(line)) {
          this.clearInvalidState(event.graphics[0]);

          const toolEventInfo = event.toolEventInfo as __esri.MoveEventInfo;
          if (toolEventInfo) {
            const moverAttr = toolEventInfo.mover.attributes;
            const movedPoint: Point = toolEventInfo.mover.geometry as Point;

            //get profile x and y values
            const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
            const dataX = (movedPoint as Point).x / xScale;
            const dataY = (movedPoint as Point).y / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
            const profileMinY = ((this.maxExtent?.ymin as number) + this.y_padding) / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);

            if (dataX < waterwayDesignStore.waterwayDesignObject.startGuardrail || dataX > waterwayDesignStore.waterwayDesignObject.endGuardrail || dataY < profileMinY) {
              MessageCenter.addWarningFromCode('WDT_73'); //warning when station moved beyond profile
              this.mainSketchVm?.undo();
              return;
            }
            if (this.checkAboveCenterline(movedPoint.x, movedPoint.y, this.centerline)) {
              await MessageCenter.addWarningFromCode('WDT_106'); //warning when station moved above centerline
            }
            if (this.verifyDistanceWithinLimit(movedPoint.x, movedPoint.y, 2, this.centerline)) {
              await MessageCenter.addWarningFromCode('WDT_110'); //warning when station moved more than 2 feet below centerline
            }
            // Calculate the vertices for the reshaped line
            const reshapedVertices = line.paths[0].map(([x, y]) => [x / xScale, y]);
            // Validate the angle between the reshaped line and the alignment centerline
            const isAngleValid = this.validateAngleBetweenLines(reshapedVertices, this.alignmentCenterLine);
            if (!isAngleValid) {
              MessageCenter.addErrorFromCode('WDT_105');
              this.mainSketchVm?.undo();
              return;
            }
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
            if (moverAttr.pointIndex >= 0) {
              this.broadcastMoveVertexInfo(newVertex, moverAttr.pointIndex, this.addWDTEdits);
            }
          }
        } else {
          event.graphics[0].symbol = reachLineSymbol.invalidLineSymbol;
          await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);


          // cleare error state
          setTimeout(() => {
            this.mainSketchVm?.undo();
            event.graphics[0].symbol = reachLineSymbol.designLineSymbol;
            this._isValidLine = true;
          }, 500);

          setTimeout(() => {
            MessageCenter.clearMessages();
          }, 1500);
        }
      }
      else if (event.toolEventInfo.type.includes('move-start')) {
      } else if (event.toolEventInfo.type.includes('move')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-remove')) {
        this.mainSketchVm?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-add')) {
        this.mainSketchVm?.undo();
        this.isAfterReshapeVertexAdd = true;
      } else if (event.toolEventInfo.type.includes('reshape-start')) {
      } else if (event.toolEventInfo.type.includes('reshape')) {
        if (this.isAfterReshapeVertexAdd) {
          this.mainSketchVm?.undo();
          this.isAfterReshapeVertexAdd = false;
        }
      }
    }
  }

  //Function to generate reachLine from saved data.
  async createReachLineFromSavedWaterway() {
    const stations = waterwayDesignStore.waterwayDesignObject.waterwayDesignStations;
    if (waterwayDesignStore.alignedDesignElementId && stations.length) {
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
      const path: [number, number][] = [];
      let minY: number = 10000000;
      let maxY: number = 0;
      stations.forEach((waterwayDesignStation: any, i: number) => {
        let stationValue;
        if (waterwayDesignStation.reachId === "START" || waterwayDesignStation.reachId === "END") {
          stationValue = waterwayDesignStation.reachId === "START" ? waterwayDesignStation.startStation : waterwayDesignStation.endStation;
        } else {
          stationValue = waterwayDesignStation.station;
        }
        if ((i > 0 && i < stations.length - 1) || (i === 0 && waterwayDesignStation.groundZ > 0) || (i === stations.length - 1 && waterwayDesignStation.groundZ > 0)) {
          path.push([parseStationString(stationValue) * xScale, parseFloat(waterwayDesignStation.channelZ) * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)]);
          if (waterwayDesignStation.channelZ < minY) {
            minY = waterwayDesignStation.channelZ;
          }
          if (waterwayDesignStation.channelZ > maxY) {
            maxY = waterwayDesignStation.channelZ;
          }
        }
      });
      await this.generateReachLineFromStations(path);
      await this.addCenterReachLabels(stations);
      this.hub.MapStore?.addStations();
    }
  }

  /** events */
  async editWaterway(info: any, propAddEdits?: any) {
    if (this.sketchLayer?.graphics && this.sketchLayer.graphics.length > 0) {
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      const formtStation = parseStationString(info.point[0]) * xScale;
      if (formtStation !== undefined) {
        currentLine.setPoint(0, (info.index - 1), [formtStation, info.point[1]]);

        //get profile x and y values
        const dataX = formtStation / xScale;
        const dataY = info.point[1] / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
        const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
        if (info.index >= 0) {
          this.broadcastMoveVertexInfo(newVertex, (info.index - 1), propAddEdits);
        }
      }
    }
  }

  async truncateWaterway(info: any, propAddEdits?: any) {
    const dataX = parseStationString(info.point[0]);
    const dataY = info.point[1] / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor);
    const newVertex: XyzPoint = new XyzPoint(dataX, dataY, 0);
    if (info.index >= 0) {
      this.broadcastMoveVertexInfo(newVertex, (info.index - 1), propAddEdits);
    }
  }

  resetGuardrail() {
    waterwayDesignStore.waterwayDesignObject.startGuardrail = toDecimalPrecision(waterwayDesignStore.startGradeLength, 1);
    waterwayDesignStore.waterwayDesignObject.endGuardrail = toDecimalPrecision((this.endStation_X - waterwayDesignStore.endGradeLength), 1);
    this.drawGuardrail();
  }

  /** supporting functions */
  private validateDirection(stations: any) {
    let preY = 0;
    let direction: "up" | "down" | "unknown" = "unknown";
    let validDirection = true;
    stations.forEach((point, index) => {
      if (index === 0) {
        preY = point[1];
      } else {
        if (point[1] > preY) {
          if (direction === "unknown") {
            direction = "up";
            preY = point[1];
          } else if (direction == "up") {
            preY = point[1];
          } else {
            validDirection = false;
            return;
          }
        } else if (point[1] < preY) {
          if (direction === "unknown") {
            direction = "down";
            preY = point[1];
          } else if (direction == "down") {
            preY = point[1];
          } else {
            validDirection = false;
            return;
          }
        } else {          // Case "flat"
          // validDirection = false;
          // return;

          preY = point[1];
        }
      }
    });
    return validDirection;
  }

  private validateAngleBetweenLines(stationVertices, alignmentCenterLine) {
    // Adjust Y values of stationVertices
    const adjustedStationVertices = stationVertices.map(point => [point[0], point[1] / (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)]);

    // Helper function to calculate dot product
    const dotProduct = (v1, v2) => {
      return v1[0] * v2[0] + v1[1] * v2[1];
    };
    // Helper function to calculate magnitude of a vector
    const magnitude = (v) => {
      return Math.sqrt(v[0] * v[0] + v[1] * v[1]);
    };

    // Helper function to calculate the angle in degrees between two vectors
    const angleBetweenVectors = (v1, v2) => {
      const dot = dotProduct(v1, v2);
      const magV1 = magnitude(v1);
      const magV2 = magnitude(v2);
      if (magV1 === 0 || magV2 === 0) {
        return 0;
      }
      const cosineTheta = dot / (magV1 * magV2);
      const angle = Math.acos(Math.max(-1, Math.min(1, cosineTheta)));
      return radiansToDegrees(angle)
    };

    // Calculate vector for the alignment line using its start and end points
    const alignmentVector = [
      alignmentCenterLine[alignmentCenterLine.length - 1][0] - alignmentCenterLine[0][0],
      (alignmentCenterLine[alignmentCenterLine.length - 1][1] - alignmentCenterLine[0][1])
    ];

    // Validate each segment of the adjustedStationVertices
    for (let i = 1; i < adjustedStationVertices.length; i++) {
      // Calculate vector for the current reach segment
      const segmentVector = [
        adjustedStationVertices[i][0] - adjustedStationVertices[i - 1][0],
        adjustedStationVertices[i][1] - adjustedStationVertices[i - 1][1]
      ];

      // Determine if the reaches segment  is being drawn from right to left
      const isRightToLeftAndUpward = adjustedStationVertices[i][0] < adjustedStationVertices[i - 1][0] && adjustedStationVertices[i][1] > adjustedStationVertices[i - 1][1];

      // Calculate the angle between the alignment vector and the segment vector
      let angle = angleBetweenVectors(alignmentVector, segmentVector);
      if (isRightToLeftAndUpward) {
        if (angle > 90) {
          angle = 180 - angle;
        }
      }

      if (angle > 45) {
        return false;
      }
    }
    return true;
  }


  private validateLastStation(stations: any, profileMinY: number) {
    let validLastPoint = true;
    stations.forEach((point, index) => {
      if (index === stations.length - 1) {
        if (point[0] > waterwayDesignStore.waterwayDesignObject.endGuardrail || point[0] < waterwayDesignStore.waterwayDesignObject.startGuardrail || point[1] < profileMinY) {
          validLastPoint = false
          return
        }
      }
    });
    return validLastPoint;
  }

  checkAboveCenterline(x: number, y: number, centerline: Polyline = this.centerline): boolean {
    let checkAbove = false;
    let found = false;
    let resultY;

    for (let i = 0; i <= centerline.paths.length - 1; i++) {
      const path = centerline.paths[i];
      for (let j = 0; j < path.length - 1; j++) {
        const start = path[j];
        const end = path[j + 1];

        if ((x >= start[0] && x <= end[0]) || (x >= end[0] && x <= start[0])) {
          resultY = start[1] + ((x - start[0]) * (end[1] - start[1])) / (end[0] - start[0]);
          found = true;
          break;
        }
      }

      if (found) {
        break;
      }
    }

    if (found && y > resultY) {
      checkAbove = true;
    }

    return checkAbove;
  }

  verifyDistanceWithinLimit(x: number, y: number, distance: number, centerline: Polyline = this.centerline): boolean {
    let isDistanceExceeded = false;
    let found = false;
    let resultY;

    for (let i = 0; i <= centerline.paths.length - 1; i++) {
      const path = centerline.paths[i];
      for (let j = 0; j < path.length - 1; j++) {
        const start = path[j];
        const end = path[j + 1];

        if ((x >= start[0] && x <= end[0]) || (x >= end[0] && x <= start[0])) {
          resultY = start[1] + ((x - start[0]) * (end[1] - start[1])) / (end[0] - start[0]);
          found = true;
          break;
        }
      }

      if (found) {
        break;
      }
    }

    if (found && y < resultY - distance * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)) {
      isDistanceExceeded = true;
    }

    return isDistanceExceeded;
  }

  private clearCrossSectionWaterwayStations() {
    threeCrossSectionDesignStore.threeCrossSectionObject.waterwayReachStations = [];
    threeCrossSectionDesignStore.threeCrossSectionObject.threeCrossSectionPoints = [];
  }

  async addReachVertex(info: any) {
    this.mainSketchVm?.complete();
    this.changePointSymbol('reachStations');
    if (this.sketchLayer && this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      const path = currentLine?.paths[0];
      // Create the new point
      const newPoint = new Point({
        x: parseFloat(info.item.station.replace('+', '')),
        y: parseFloat(info.item.channelZ) * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
        spatialReference: currentLine.spatialReference
      });

      if (path[0][0] > path[path.length - 1][0]) {
        //digitized right to left, correct the order of pointIndex
        info.index = (path.length) - info.index;
      }

      // Add the new point to the polyline at the specified index
      currentLine.insertPoint(0, info.index, newPoint);
      const newLine = new Polyline({
        paths: currentLine.paths,
        spatialReference: currentLine.spatialReference,
      });
      this.sketchLayer.graphics.getItemAt(0).geometry = newLine;
    }
    this.createReachLineFromSavedWaterway();
    const reachStationsLayer = this._profileView?.map.findLayerById('deleteReachStations') as FeatureLayer;
    if (reachStationsLayer) {
      this.addPoint();
    }
  }

  private get profileTrackLayer(): GraphicsLayer | undefined {
    const id = 'editing_track_lyr';
    const title = 'editing track';

    if (!this._profileTrackLayer) {
      const lyr = this._profileView?.map.findLayerById(id);
      if (lyr) {
        this._profileTrackLayer = lyr as GraphicsLayer;
      } else {
        this._profileTrackLayer = new GraphicsLayer({ id, title, listMode: 'hide' });
        this._profileView?.map.add(this._profileTrackLayer);
      }
    }

    return this._profileTrackLayer;
  }

  public unTrackProfilePoint(id: string) {
    const graphicToRemove = this.profileTrackLayer?.graphics.find(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    });
    if (graphicToRemove) {
      this.profileTrackLayer?.graphics.remove(graphicToRemove);
    }
  }

  public trackStation(station: any) {
    if (!(station.reachId === "START")) {
      if (!waterwayDesignStore.selectedWaterwayStations.some(e => e === station.reachId)) {
        const stationX = parseFloat(station.station.replace('+', '')) * (designStore.commonHorizontalStretchFactor || 1);
        const point = new Point({
          x: stationX,
          y: station.channelZ * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
          spatialReference: this._profileView?.spatialReference
        });
        this.trackProfilePoint(point, station.reachId);
      }
    }
  }

  public trackProfilePoint(point: Point, id: string) {
    if (!this.profileTrackLayer?.graphics.toArray().some(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    })) {
      const graphic = new Graphic({
        geometry: point,
        symbol: reachHighlightSymbol,
        attributes: { id: id }
      });
      this.profileTrackLayer?.graphics.push(graphic);
    }
  }

  public unTrackProfilePoints() {
    this.profileTrackLayer?.graphics?.removeAll();
  }

  public unTrackUnselectedProfilePoints() {
    const idsToDelete = this.profileTrackLayer?.graphics.toArray().filter(g => {
      return (
        g.attributes &&
        g.attributes.id &&
        !waterwayDesignStore.selectedWaterwayStations.some(s => s === g.attributes.id)
      );
    }).map(g => { return g.attributes.id; });
    if (idsToDelete) {
      for (const id of idsToDelete) {
        this.unTrackProfilePoint(id);
      }
    }
  }

  cleanUp() {
    this.viewPointerMoveHandle?.remove();
    this.viewPointerLeaveHandle?.remove();
    this.removeSketch();
    if (this.sketchLayer) {
      this._profileView?.map?.remove(this.sketchLayer);
      this.sketchLayer.destroy();
      this.sketchLayer = void 0;
    }
  }

  Dispose() {
    this.cleanUp();
    super.Dispose();
    this.mainSketchVm?.destroy();
  }

  // ai design experimental code
  /* istanbul ignore next */
  public async flashAiAction(point: Point, id: string, moveDirection: aiMove, color: number[], arrowX: number, arrowY: number) {
    const graphic = new Graphic({
      geometry: point,
      symbol: createAiActionArrow(moveDirection, color, arrowX, arrowY),
      attributes: { id: id }
    });
    for (let index = 0; index < 5; index++) {
      this.profileTrackLayer?.graphics.push(graphic);
      await sleep(300);
      this.profileTrackLayer?.graphics?.removeAll();
      await sleep(200);
    }
  }

  /* istanbul ignore next */
  public async bubbleUpAiActionLabel(point: Point, id: string, text: string, start: number, color: number[]) {
    for (let index = 0; index < 100; index++) {
      const gradColor = index > 10 ? color.map(e => Math.min(e + index * 2, 255)) : color;
      const graphic = new Graphic({
        geometry: point,
        symbol: createAiActionLabel(text, gradColor, start + index),
        attributes: { id: id }
      });
      this.aiActionLabelLayer?.graphics.push(graphic);
      await sleep(17);
      this.aiActionLabelLayer?.graphics?.removeAll();
    }
  }

  /* istanbul ignore next */
  public zoomToAction(point: Point) {
    const delta = 20;
    const xmin = point.x - delta;
    const xmax = point.x + delta;
    const ymin = point.y - delta;
    const ymax = point.y + delta;
    const extent = new Extent({ xmin: xmin, xmax: xmax, ymin: ymin, ymax: ymax, spatialReference: this._profileView!.spatialReference });
    this._profileView!.goTo(extent);
  }

  /* istanbul ignore next */
  public zoomToMaxExtent() {
    if (this.maxExtent) {
      this._profileView!.extent = this.maxExtent;
    }
  }

  /* istanbul ignore next */
  public turnOffWaterwayReaches = () => {
    this.toggleReaches(false);
  }

  /* istanbul ignore next */
  public addAiReaches(stations: { reachId: string; station: number; channelElev: number }[],
    clearGraphicLayer = true,
    symbols = { point: reachStationSymbol.reachStationSymbol, line: reachLineSymbol.designLineSymbol }) {

    if (stations.length > 0) {
      const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
      const paths: [number, number][] = [];
      const pointGraphics: Graphic[] = [];
      let minY: number = 10000000;
      let maxY: number = 0;
      stations.forEach((stn) => {

        paths.push([stn.station * xScale, stn.channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)]);
        if (stn.channelElev < minY) {
          minY = stn.channelElev;
        }
        if (stn.channelElev > maxY) {
          maxY = stn.channelElev;
        }

        const geom = new Point({
          x: stn.station * xScale,
          y: stn.channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
          spatialReference: this.spatialReference
        })

        const graphic = new Graphic({
          geometry: geom,
          symbol: symbols.point
        });

        pointGraphics.push(graphic);
      });
      const polyline = new Polyline({
        paths: [paths],
        spatialReference: this.spatialReference
      });

      // Create a polyline graphic
      const polylineGraphic = new Graphic({
        geometry: polyline,
        symbol: symbols.line
      });

      this.setSketchLayer();
      if (clearGraphicLayer) this.sketchLayer?.graphics.removeAll();
      this.sketchLayer?.graphics.add(polylineGraphic);
      this.sketchLayer?.graphics.addMany(pointGraphics);
      // this.hub.MapStore?.addStations();
    }
  }

  /* istanbul ignore next */
  public async addAiReachesPiecewise(rawStations: { reachId: string; station: number; channelElev: number }[]) {

    for (let index = 0; index < rawStations.length - 1; index++) {
      const stations: { reachId: string; station: number; channelElev: number }[] = [];
      for (let j = 0; j <= index + 1; j++) {
        stations.push(rawStations[j])
      }
      if (stations.length > 0) {
        const xScale = designStore.commonHorizontalStretchFactor ? designStore.commonHorizontalStretchFactor : 1;
        const paths: [number, number][] = [];
        const pointGraphics: Graphic[] = [];
        let minY: number = 10000000;
        let maxY: number = 0;
        stations.forEach((stn) => {

          paths.push([stn.station * xScale, stn.channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor)]);
          if (stn.channelElev < minY) {
            minY = stn.channelElev;
          }
          if (stn.channelElev > maxY) {
            maxY = stn.channelElev;
          }

          const geom = new Point({
            x: stn.station * xScale,
            y: stn.channelElev * (designStore.commonVerticalStretchFactor || AppConstants.profileStretchFactor),
            spatialReference: this.spatialReference
          })

          const graphic = new Graphic({
            geometry: geom,
            symbol: reachStationSymbol.reachStationSymbol
          });

          pointGraphics.push(graphic);
        });
        const polyline = new Polyline({
          paths: [paths],
          spatialReference: this.spatialReference
        });

        const symbol = reachLineSymbol.designLineSymbol;
        // Create a polyline graphic
        const polylineGraphic = new Graphic({
          geometry: polyline,
          symbol: symbol
        });

        this.setSketchLayer();
        this.sketchLayer?.graphics.removeAll();
        this.sketchLayer?.graphics.add(polylineGraphic);
        this.sketchLayer?.graphics.addMany(pointGraphics);
        // this.hub.MapStore?.addStations();
      }
      await sleep(750);
    }


  }

  /* istanbul ignore next */
  private _aiActionLabelLayer: GraphicsLayer | undefined = void 0;
  /* istanbul ignore next */
  private get aiActionLabelLayer(): GraphicsLayer | undefined {
    const id = 'ai_action_label_lyr';
    const title = 'action label';

    if (!this._aiActionLabelLayer) {
      const lyr = this._profileView?.map.findLayerById(id);
      if (lyr) {
        this._aiActionLabelLayer = lyr as GraphicsLayer;
      } else {
        this._aiActionLabelLayer = new GraphicsLayer({ id, title, listMode: 'hide' });
        this._profileView?.map.add(this._aiActionLabelLayer);
      }
    }

    return this._aiActionLabelLayer;
  }
  // End: ai design experimental code
}

type aiMove = 0 | 180 | 270 | 90;

export { DynamicWaterwayProfileStore };
export type { aiMove };

